import {
  CORP_HOMEPAGE_SUBTITLE,
  CORP_HOMEPAGE_TITLE,
  getConfigTenant,
  isCorpTenant,
  useGetUserMetadataQuery,
  useUpdateUserMetadataMutation,
} from "@capone/common";
import { Box, Typography } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import clsx from "clsx";
import {
  BannerSeverity,
  CorporateTypography,
  NotificationBanner,
  useDeviceTypes,
} from "halifax";
import React, { useContext, useEffect } from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  CallState,
  PackagesEntryTypeEnum,
  VIEWED_PACKAGES_SEARCH,
} from "redmond";
import { AVAILABLE, getExperimentVariant } from "@capone/experiments";
import { fetchFlightLocation } from "../../api/v0/search/fetchFlightLocation";
import { fetchHotelLocation } from "../../api/v0/search/fetchHotelLocation";
import { trackEvent } from "../../api/v0/trackEvent";
import { ClientContext } from "../../App";
import { useExperiments, XMOD_V1_EXPERIMENT } from "../../context/experiments";
import { PORTAL_TITLE, SEARCH_PACKAGE_TITLE } from "../../lang/textConstants";
import { listPaymentMethods } from "../availability/actions/actions";
import { MobilePackagesSearchControl } from "./components";
import { HowPackagesWork } from "./components/HowPackagesWork";
import { PackagesSearchControl } from "./components/PackagesSearchControl";
import { PackagesSearchConnectorProps } from "./container";
import "./styles.scss";
import { CONFIRM_TRAVELER_DETAILS, SUBTITLE, TITLE } from "./textConstants";
import {
  IPackagesParsedQuery,
  parseQueryString,
} from "./utils/parseQueryString";

export interface IPackagesSearchProps
  extends PackagesSearchConnectorProps,
    RouteComponentProps {}

export const PackagesSearch = ({
  largestValueAccount,
  fetchRewardsAccountsCallState,
  fetchRewardsAccounts,
  resetPackagesAvailabilityCallState,
  setDepartureDate,
  setReturnDate,
  setOrigin,
  setDestination,
  setFareclassOptionFilter,
  setStopsOption,
  history,
  setTravelers,
}: IPackagesSearchProps) => {
  const { matchesMobile } = useDeviceTypes();
  const { sessionInfo } = useContext(ClientContext);

  const isCorporate = isCorpTenant(getConfigTenant());

  const {
    data: userPreferenceMetadata,
    isLoading: userPreferencesMetadataLoading,
  } = useGetUserMetadataQuery({
    keys: ["hasSeenPackagesFtux"],
  });

  const [updateUserMetadata] = useUpdateUserMetadataMutation();

  const expState = useExperiments();
  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const getSubtitleToDisplay = () => {
    if (
      (fetchRewardsAccountsCallState === CallState.Success ||
        fetchRewardsAccountsCallState === CallState.Failed) &&
      largestValueAccount
    ) {
      return SUBTITLE(largestValueAccount);
    }
    return <Skeleton className="subtitle-loading" />;
  };

  const parsedQueryString = parseQueryString(history);

  const populateFieldsFromQueryString = async ({
    fromDate,
    untilDate,
    origin,
    destination,
    stopsOption,
    fareClass,
    adultsCount,
    childrenCount,
    infantsInSeatCount,
    infantsOnLapCount,
  }: IPackagesParsedQuery) => {
    if (fromDate) {
      setDepartureDate(fromDate);
    }

    if (untilDate) {
      setReturnDate(untilDate);
    }

    if (origin) {
      const originLocation = await fetchFlightLocation(origin);
      setOrigin(originLocation);
    }

    if (destination) {
      const destinationLocation = await fetchHotelLocation(destination);
      setDestination(destinationLocation);
    }

    if (stopsOption) {
      setStopsOption(stopsOption);
    }

    if (fareClass) {
      setFareclassOptionFilter({
        basic: false,
        standard: false,
        enhanced: false,
        premium: false,
        luxury: false,
        [fareClass]: true,
      });
    }
    if (
      adultsCount ||
      childrenCount ||
      infantsInSeatCount ||
      infantsOnLapCount
    ) {
      // hardcoding to be age of 1 for infants and 11 for children per the ticket [CMKT-4767]
      const infantsArr = [
        ...(infantsInSeatCount
          ? new Array(infantsInSeatCount ?? 0).fill({ age: 1, inSeat: true })
          : []),
        ...(infantsOnLapCount
          ? new Array(infantsOnLapCount ?? 0).fill({ age: 1, inSeat: false })
          : []),
      ];
      setTravelers({
        adultsCount,
        children: childrenCount ? new Array(childrenCount).fill(11) : [],
        infants: infantsArr,
      });
    }
  };

  useEffect(() => {
    document.title = SEARCH_PACKAGE_TITLE;
    fetchRewardsAccounts(false, sessionInfo);
    listPaymentMethods();

    setTimeout(() => window.scrollTo(0, 0), 0);

    resetPackagesAvailabilityCallState();

    if (parsedQueryString) {
      (async () => {
        await populateFieldsFromQueryString(parsedQueryString);
      })();
    }

    trackEvent({
      eventName: VIEWED_PACKAGES_SEARCH,
      properties: {
        ...(!!parsedQueryString?.entryPoint && {
          entry_point: parsedQueryString.entryPoint as PackagesEntryTypeEnum,
        }),
      },
    });

    return () => {
      document.title = PORTAL_TITLE;
    };
  }, []);

  useEffect(() => {
    if (
      !userPreferencesMetadataLoading &&
      !userPreferenceMetadata?.userMetadata?.hasSeenPackagesFtux
    ) {
      updateUserMetadata({ userMetadata: { hasSeenPackagesFtux: true } });
    }
  }, [userPreferenceMetadata?.userMetadata]);

  if (matchesMobile)
    return (
      <Box className="mobile-packages-search-root">
        <MobilePackagesSearchControl />
      </Box>
    );

  return (
    <Box className="packages-search-root">
      <Box
        className={clsx("packages-search-container-desktop", {
          "capone-corporate": isCorporate,
          xmod: isXmodRedesign,
        })}
      >
        <Box className="packages-search-titles-and-fields-wrapper">
          {isCorporate ? (
            <>
              <CorporateTypography
                variant="h1"
                className="search-title corporate"
                component="h1"
              >
                {CORP_HOMEPAGE_TITLE}
              </CorporateTypography>
              <CorporateTypography
                variant="h5"
                className="search-subtitle corporate"
                fontWeight="light"
                component="h2"
              >
                {CORP_HOMEPAGE_SUBTITLE}
              </CorporateTypography>
            </>
          ) : (
            <>
              <Typography variant="h1" className="search-title">
                {TITLE}
              </Typography>
              <Typography className="search-subtitle">
                {getSubtitleToDisplay()}
              </Typography>
            </>
          )}

          <PackagesSearchControl />
          {(parsedQueryString?.childrenCount ||
            parsedQueryString?.infantsInSeatCount ||
            parsedQueryString?.infantsOnLapCount) &&
          parsedQueryString.entryPoint ? (
            <NotificationBanner
              className="confirm-travelers-banner"
              label={CONFIRM_TRAVELER_DETAILS}
              severity={BannerSeverity.WARNING}
            />
          ) : undefined}
          {!userPreferencesMetadataLoading &&
          !userPreferenceMetadata?.userMetadata?.hasSeenPackagesFtux ? (
            <HowPackagesWork />
          ) : undefined}
        </Box>
      </Box>
    </Box>
  );
};
